import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type RefObject } from 'react';
import { Link, useParams } from 'react-router';
import { ApiError, meetingsApi, type MeetingBreakouts, type MeetingConnect, type MeetingDoor } from '../api/client';
import { AddToCalendar } from '../components/AddToCalendar';
import { Face } from '../components/PhotoEditor';
import { Button, Loading, Notice } from '../components/ui';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import {
  cameraOf,
  clearMove,
  setRecording,
  connectMeeting,
  leaveMeeting,
  raiseHand,
  resetMeeting,
  screenOf,
  startMeetingSound,
  toggleMeetingCamera,
  toggleMeetingMute,
  toggleMeetingShare,
  useMeeting,
  watchMeetingScreen,
  type MeetingPerson,
  type MeetingState
} from '../lib/meeting';
import { galleryLayout } from '../lib/patchStrip';
import { clockText } from '../lib/patchText';
import { useTicking } from '../lib/patch';

/**
 * A meeting's own page (/m/<code>; 1.13.0, Teampace 2707), for everyone with the link. People
 * signed in to the meeting's company join straight away; anyone else gives their name and waits in
 * the lobby until someone from the company lets them in. Then the room: everyone as a tile, a shared
 * screen as the stage, the controls, and a panel of who's in (and, for the company's people, who's
 * waiting). Guests see the meeting and nothing else of Stitch. Marked private: live help and session
 * recordings never include a meeting.
 */

/** A guest's place in the lobby, kept for this tab (a reload keeps it). */
interface GuestTicket {
  guestId: string;
  key: string;
}
const ticketKey = (code: string) => `stitch:meeting:${code}`;
function savedTicket(code: string): GuestTicket | null {
  try {
    const v = JSON.parse(sessionStorage.getItem(ticketKey(code)) ?? 'null') as GuestTicket | null;
    return v && typeof v.guestId === 'string' && typeof v.key === 'string' ? v : null;
  } catch {
    return null;
  }
}
function saveTicket(code: string, t: GuestTicket | null): void {
  try {
    if (t) sessionStorage.setItem(ticketKey(code), JSON.stringify(t));
    else sessionStorage.removeItem(ticketKey(code));
  } catch {
    // Private window: the place lasts as long as the page.
  }
}
const NAME_KEY = 'stitch:meeting-name';
function savedName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

type Step = { at: 'door' } | { at: 'asking' } | { at: 'lobby'; ticket: GuestTicket } | { at: 'denied' } | { at: 'room' } | { at: 'gone'; why: string };

/** What the person in the room may do beyond the controls: the company's people see the lobby; the host or an admin manage people. */
interface Inside {
  title: string;
  member: { tenant: string; id: string; canChange: boolean; webinar: boolean; presenters: string[]; breakouts: MeetingBreakouts | null; recordable: boolean } | null;
  ticket: GuestTicket | null;
}

export function MeetingPage() {
  const { code = '' } = useParams();
  const { state: auth } = useAuth();
  const [door, setDoor] = useState<MeetingDoor | null>(null);
  const [doorError, setDoorError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>({ at: 'door' });
  const [inside, setInside] = useState<Inside | null>(null);
  const [error, setError] = useState<string | null>(null);
  const meeting = useMeeting();

  useEffect(() => {
    document.title = door ? `${door.title} · Meeting` : 'Meeting';
  }, [door]);

  // What the link is (again once signed in: they may be in its company).
  useEffect(() => {
    let live = true;
    meetingsApi.door(code).then(
      (d) => live && (setDoor(d), setDoorError(null)),
      (err) => live && setDoorError(err instanceof ApiError && err.status !== 404 && err.status !== 0 ? err.message : 'That meeting link isn’t working. Check it with the person who sent it.')
    );
    return () => {
      live = false;
    };
  }, [code, auth?.signedIn]);

  // A guest already waiting (this tab was reloaded): back to the lobby.
  useEffect(() => {
    if (door && !door.member && step.at === 'door') {
      const t = savedTicket(code);
      if (t) setStep({ at: 'lobby', ticket: t });
    }
  }, [door, code, step.at]);

  // Out of the room (left, taken out, the meeting ended): say so.
  useEffect(() => {
    if (step.at === 'room' && meeting.phase === 'ended') {
      if (inside?.ticket) {
        void meetingsApi.leave(code, inside.ticket.guestId, inside.ticket.key).catch(() => undefined);
        saveTicket(code, null);
      }
      setStep({ at: 'gone', why: meeting.notice ?? 'You left the meeting.' });
    }
  }, [meeting.phase, meeting.notice, step.at, inside, code]);

  // Leaving the page leaves the meeting.
  useEffect(() => () => void leaveMeeting().then(resetMeeting), []);

  const enter = async (join: MeetingConnect, next: Inside) => {
    setInside(next);
    setStep({ at: 'room' });
    await connectMeeting(join);
  };

  const joinAsMember = async () => {
    if (!door?.member) return;
    setError(null);
    try {
      const j = await meetingsApi.join(door.member.tenant, door.member.id);
      await enter(j, {
        title: j.meeting.title,
        member: {
          tenant: door.member.tenant,
          id: door.member.id,
          canChange: j.meeting.canChange,
          webinar: j.meeting.kind === 'webinar',
          presenters: j.meeting.presenters,
          breakouts: j.meeting.breakouts,
          recordable: j.recordable
        },
        ticket: null
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The meeting couldn’t start.');
    }
  };

  if (doorError) return <Frame title="Meeting">{<Notice tone="crit" title="This link isn’t working">{doorError}</Notice>}</Frame>;
  if (!door) return <Frame title="Meeting">{<Loading label="Opening the meeting" />}</Frame>;

  /**
   * To another room of the meeting (a breakout room, or back with 0): a token for it, then over,
   * microphone and camera as they were. Guests go where Stitch says; people in the company to the
   * room asked for (the host may visit any).
   */
  const move = async (to: number): Promise<string | null> => {
    clearMove();
    try {
      if (inside?.member) {
        const j = await meetingsApi.join(inside.member.tenant, inside.member.id, to);
        setInside((i) => (i?.member ? { ...i, member: { ...i.member, breakouts: j.meeting.breakouts } } : i));
        await connectMeeting(j, true);
      } else if (inside?.ticket) {
        const r = await meetingsApi.status(code, inside.ticket.guestId, inside.ticket.key);
        if (r.state !== 'in' || !r.join) {
          await leaveMeeting();
          return null;
        }
        await connectMeeting(r.join, true);
      }
      return null;
    } catch (err) {
      return err instanceof Error ? err.message : 'That room couldn’t be reached. Try again.';
    }
  };
  const setBreakouts = (b: MeetingBreakouts | null) => setInside((i) => (i?.member ? { ...i, member: { ...i.member, breakouts: b } } : i));

  if (step.at === 'room' && inside) return <Room s={meeting} inside={inside} code={code} move={move} setBreakouts={setBreakouts} />;

  if (step.at === 'gone')
    return (
      <Frame title={door.title} sub={door.company}>
        <Notice title={step.why} />
        <div className="row-actions">
          <Button variant="primary" icon="video" onClick={() => (resetMeeting(), setStep({ at: 'door' }))}>
            Join again
          </Button>
          {auth?.signedIn && door.member && <Link to={`/t/${encodeURIComponent(door.member.tenant)}/meetings`}>Back to Meetings</Link>}
        </div>
      </Frame>
    );

  if (step.at === 'lobby')
    return (
      <Lobby
        door={door}
        code={code}
        ticket={step.ticket}
        onIn={(j) => void enter(j, { title: j.title, member: null, ticket: step.ticket })}
        onDenied={() => (saveTicket(code, null), setStep({ at: 'denied' }))}
        onCancel={() => {
          void meetingsApi.leave(code, step.ticket.guestId, step.ticket.key).catch(() => undefined);
          saveTicket(code, null);
          setStep({ at: 'door' });
        }}
      />
    );

  if (step.at === 'denied')
    return (
      <Frame title={door.title} sub={door.company}>
        <Notice tone="warn" title="You weren’t let in">Someone in the meeting said no. If you think that’s a mistake, contact the person who sent you the link.</Notice>
      </Frame>
    );

  return (
    <Frame title={door.title} sub={`${door.company} · ${door.kind === 'webinar' ? 'Webinar' : 'Meeting'} with ${door.hostName}`}>
      {door.startsAt && !door.live && <p className="muted">Starts {new Date(door.startsAt).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' })}</p>}
      {door.startsAt && door.endsAt && !door.live && Date.parse(door.endsAt) > Date.now() && (
        <div>
          <AddToCalendar m={{ title: door.title, startsAt: door.startsAt, endsAt: door.endsAt, link: window.location.href.split('?')[0]!, company: door.company }} ics={meetingsApi.doorIcsUrl(code)} />
        </div>
      )}
      {door.live && <p className="meeting-live-now">Going on now</p>}
      {door.recording && (
        <Notice tone="warn" title="This meeting is being recorded">
          By joining, you’ll be in the recording.
        </Notice>
      )}
      {error && <Notice tone="crit">{error}</Notice>}
      {door.member ? (
        <div className="stack">
          <p>You’re signed in as {auth?.user?.name ?? 'yourself'}, in {door.company}.</p>
          <Button variant="primary" icon="video" onClick={() => void joinAsMember()}>
            Join now
          </Button>
        </div>
      ) : (
        <AskToJoin
          code={code}
          lobby={door.lobby}
          busy={step.at === 'asking'}
          onAsking={() => setStep({ at: 'asking' })}
          onAsked={(t) => {
            saveTicket(code, t);
            setStep({ at: 'lobby', ticket: t });
          }}
          onError={(msg) => (setError(msg), setStep({ at: 'door' }))}
        />
      )}
      <p className="meeting-fine">Your camera starts off.{door.recording ? '' : ' If the meeting is recorded, everyone in it is told.'}</p>
    </Frame>
  );
}

/** The page around everything before the room: the meeting's name and the company's. */
function Frame({ title, sub, children }: { title: string; sub?: string; children: React.ReactNode }) {
  return (
    <main className="meeting-door" data-private="">
      <section className="meeting-door-card">
        <span className="meeting-door-icon" aria-hidden="true">
          <Icon name="video" size={22} />
        </span>
        <h1>{title}</h1>
        {sub && <p className="muted">{sub}</p>}
        {children}
      </section>
    </main>
  );
}

function AskToJoin({ code, lobby, busy, onAsking, onAsked, onError }: { code: string; lobby: boolean; busy: boolean; onAsking: () => void; onAsked: (t: GuestTicket) => void; onError: (msg: string) => void }) {
  const [name, setName] = useState(savedName);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return onError('Say your name, so they know who’s asking to come in.');
    onAsking();
    try {
      localStorage.setItem(NAME_KEY, name.trim());
    } catch {
      // Not kept: asked again next time.
    }
    try {
      const r = await meetingsApi.ask(code, name.trim());
      onAsked({ guestId: r.guestId, key: r.key });
    } catch (err) {
      onError(err instanceof Error ? err.message : 'That didn’t go through. Try again.');
    }
  };
  return (
    <form className="stack" onSubmit={(e) => void submit(e)}>
      <div className="field">
        <label htmlFor="meeting-name">Your name</label>
        <input id="meeting-name" className="input" autoComplete="name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <Button variant="primary" icon="video" type="submit" disabled={busy}>
        {busy ? 'Asking…' : lobby ? 'Ask to join' : 'Join now'}
      </Button>
    </form>
  );
}

/** Waiting to be let in: asks again every few seconds. */
function Lobby({ door, code, ticket, onIn, onDenied, onCancel }: { door: MeetingDoor; code: string; ticket: GuestTicket; onIn: (j: MeetingConnect & { title: string }) => void; onDenied: () => void; onCancel: () => void }) {
  const [problem, setProblem] = useState<string | null>(null);
  const done = useRef(false);
  // The latest answers, without asking again each time the page draws.
  const on = useRef({ onIn, onDenied });
  on.current = { onIn, onDenied };
  useEffect(() => {
    done.current = false;
    let timer: number | undefined;
    const ask = async () => {
      try {
        const r = await meetingsApi.status(code, ticket.guestId, ticket.key);
        if (done.current) return;
        setProblem(null);
        if (r.state === 'in' && r.join) {
          done.current = true;
          return on.current.onIn(r.join);
        }
        if (r.state === 'denied' || r.state === 'left') {
          done.current = true;
          return on.current.onDenied();
        }
      } catch (err) {
        if (done.current) return;
        // The place is gone (the meeting was cancelled, or the key no longer matches): start over.
        if (err instanceof ApiError && err.status === 404) {
          done.current = true;
          return on.current.onDenied();
        }
        setProblem('Can’t reach the meeting just now. Still trying…');
      }
      timer = window.setTimeout(() => void ask(), document.hidden ? 6000 : 2500);
    };
    void ask();
    return () => {
      done.current = true;
      window.clearTimeout(timer);
    };
  }, [code, ticket.guestId, ticket.key]);
  return (
    <Frame title={door.title} sub={door.company}>
      <div className="meeting-waiting" role="status">
        <span className="meeting-pulse" aria-hidden="true" />
        <p>
          <strong>Waiting for someone to let you in</strong>
          <br />
          <span className="muted">They know you’re here. This page lets you in by itself.</span>
        </p>
      </div>
      {problem && <Notice tone="warn">{problem}</Notice>}
      <Button variant="ghost" onClick={onCancel}>
        Leave the lobby
      </Button>
    </Frame>
  );
}

// --- the room -----------------------------------------------------------------------------------

function useSize(ref: RefObject<HTMLElement | null>): { w: number; h: number } | null {
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => setSize((was) => (was && was.w === el.clientWidth && was.h === el.clientHeight ? was : { w: el.clientWidth, h: el.clientHeight }));
    measure();
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, [ref]);
  return size;
}

/** People in Stitch show their photo (to the company's own people); guests and everyone else: initials. */
function Tile({ p, photos }: { p: MeetingPerson; photos: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  const track = p.camera ? cameraOf(p.id) : null;
  useEffect(() => {
    const el = ref.current;
    if (!track || !el) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [track]);
  return (
    <figure className={`patch-tile meeting-tile${p.speaking ? ' speaking' : ''}${track ? '' : ' no-video'}`} aria-label={`${p.name}${p.guest ? ' (guest)' : ''}${p.me ? ' (you)' : ''}${p.muted ? ', muted' : ''}`}>
      {track ? <video ref={ref} autoPlay playsInline muted className={p.me ? 'mirror' : undefined} /> : <Face userId={photos && !p.guest ? p.id : null} name={p.name} size="medium" className="patch-tile-avatar" />}
      <figcaption aria-hidden="true">
        {p.hand && <span title="Hand raised">✋</span>}
        <span>
          {p.name}
          {p.me && ' (you)'}
        </span>
        {p.guest && <span className="meeting-guest-tag">Guest</span>}
        {p.muted && p.publish && <Icon name="micOff" size={13} />}
      </figcaption>
    </figure>
  );
}

function ScreenStage({ id, name }: { id: string; name: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const track = screenOf(id);
  useEffect(() => {
    const el = ref.current;
    if (!track || !el) return;
    track.attach(el);
    return () => void track.detach(el);
  }, [track]);
  return (
    <figure className="meeting-screen" aria-label={`${name}’s screen`}>
      <video ref={ref} autoPlay playsInline muted />
      <figcaption>{name}’s screen</figcaption>
    </figure>
  );
}

function Gallery({ people, photos }: { people: MeetingPerson[]; photos: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const size = useSize(box);
  // A webinar's watchers aren't tiles: the people talking are.
  const shown = people.filter((p) => p.publish || p.me);
  const fit = size ? galleryLayout(Math.max(shown.length, 1), size.w, size.h) : null;
  return (
    <div ref={box} className="meeting-gallery" style={fit ? { gridTemplateColumns: `repeat(${fit.cols}, ${fit.width}px)`, gridAutoRows: `${fit.height}px` } : undefined}>
      {shown.map((p) => (
        <Tile key={p.id} p={p} photos={photos} />
      ))}
    </div>
  );
}

function Room({ s, inside, code, move, setBreakouts }: { s: MeetingState; inside: Inside; code: string; move: (to: number) => Promise<string | null>; setBreakouts: (b: MeetingBreakouts | null) => void }) {
  const now = useTicking(s.phase === 'connected' || Boolean(s.moving));
  const [panel, setPanel] = useState<null | 'people' | 'breakouts'>(null);
  // Names of everyone seen in the meeting, for breakout rooms the host isn't in.
  const names = useRef(new Map<string, string>());
  for (const p of s.people) names.current.set(p.id, p.name);
  // Stitch said to move: after the countdown (or Go now), once.
  const moving = useRef<number | null>(null);
  const go = async (to: number) => {
    if (moving.current !== null) return;
    moving.current = to;
    const why = await move(to);
    moving.current = null;
    if (why) setProblem(why);
  };
  useEffect(() => {
    if (s.moving && now >= s.moving.at) void go(s.moving.to);
  });
  const [waiting, setWaiting] = useState<{ id: string; name: string }[]>([]);
  const [presenters, setPresenters] = useState<string[]>(inside.member?.presenters ?? []);
  const [problem, setProblem] = useState<string | null>(null);
  const member = inside.member;
  const watching = s.watching ? s.screens.find((x) => x.id === s.watching) : null;

  // The company's people see who's waiting in the lobby.
  useEffect(() => {
    if (!member) return;
    let live = true;
    let timer: number | undefined;
    const look = async () => {
      try {
        const r = await meetingsApi.lobby(member.tenant, member.id);
        if (live) setWaiting(r.waiting);
      } catch {
        // Next time.
      }
      if (live) timer = window.setTimeout(() => void look(), document.hidden ? 10_000 : 4000);
    };
    void look();
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [member]);

  // A guest closing the tab: out of the lobby list at once (LiveKit hears it too).
  useEffect(() => {
    const t = inside.ticket;
    if (!t) return;
    const bye = () => void fetch(`/api/m/${encodeURIComponent(code)}/guest/${encodeURIComponent(t.guestId)}/leave`, { method: 'POST', keepalive: true, headers: { 'Content-Type': 'application/json', 'x-stitch-request': '1', 'x-meeting-key': t.key }, body: '{}' }).catch(() => undefined);
    window.addEventListener('pagehide', bye);
    return () => window.removeEventListener('pagehide', bye);
  }, [inside.ticket, code]);

  const act = async (what: () => Promise<unknown>) => {
    setProblem(null);
    try {
      await what();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    }
  };
  const answer = (id: string, letIn: boolean) =>
    member &&
    act(async () => {
      await meetingsApi.answer(member.tenant, member.id, id, letIn);
      setWaiting((w) => w.filter((x) => x.id !== id));
    });

  const seconds = s.startedAt ? Math.max(0, Math.round((now - s.startedAt) / 1000)) : 0;
  const status = s.phase === 'joining' ? (s.room ? `Going to ${s.roomName}…` : 'Joining…') : s.phase === 'reconnecting' ? 'Reconnecting…' : clockText(seconds);
  const breakouts = member?.breakouts ?? null;
  const canBreak = Boolean(member?.canChange && !member.webinar);
  const canRecord = Boolean(member?.recordable && s.room === 0);
  const [recBusy, setRecBusy] = useState(false);
  const record = async (on: boolean) => {
    if (!member) return;
    setRecBusy(true);
    setProblem(null);
    try {
      setRecording((await meetingsApi.record(member.tenant, member.id, on)).recording);
    } catch (err) {
      setProblem(err instanceof Error ? err.message : 'Recording didn’t change. Try again.');
    } finally {
      setRecBusy(false);
    }
  };
  const hands = s.people.filter((p) => p.hand && !p.me);

  return (
    <main className="meeting-room" data-private="">
      <header className="meeting-top">
        <div className="cell-stack">
          <strong>
            {inside.title}
            {s.room > 0 && <span className="meeting-room-name"> · {s.roomName}</span>}
          </strong>
          <small>
            {status} · {s.people.length === 1 ? 'Just you' : `${s.people.length} people`}
          </small>
        </div>
        <Button variant="danger" icon="phoneOff" onClick={() => void leaveMeeting()}>
          Leave
        </Button>
      </header>
      {s.recording && s.room === 0 && (
        <p className="meeting-recording" role="status">
          <span className="meeting-rec-dot" aria-hidden="true" /> This meeting is being recorded
        </p>
      )}
      {s.needsClick && (
        <button type="button" className="meeting-sound" onClick={() => void startMeetingSound()}>
          <Icon name="volume" size={16} /> Click to hear the meeting
        </button>
      )}
      {s.moving && (
        <div className="meeting-knock" role="status">
          <span>
            {s.moving.to ? (
              <>
                Moving you to <strong>{s.moving.name}</strong>
              </>
            ) : (
              'Back to the main room'
            )}{' '}
            in {Math.max(0, Math.ceil((s.moving.at - now) / 1000))} s
          </span>
          <Button size="small" variant="primary" onClick={() => void go(s.moving!.to)}>
            Go now
          </Button>
        </div>
      )}
      {s.room > 0 && !s.moving && (
        <div className="meeting-knock" role="status">
          <span>
            You’re in the breakout room <strong>{s.roomName}</strong>.
          </span>
          {member && (
            <Button size="small" variant="ghost" onClick={() => void go(0)}>
              Back to the main room
            </Button>
          )}
        </div>
      )}
      {member && waiting.length > 0 && !panel && (
        <div className="meeting-knock" role="status">
          <span>
            <strong>{waiting[0]!.name}</strong>
            {waiting.length > 1 ? ` and ${waiting.length - 1} more` : ''} {waiting.length > 1 ? 'are' : 'is'} waiting
          </span>
          <Button size="small" variant="primary" onClick={() => void answer(waiting[0]!.id, true)}>
            Let in
          </Button>
          <Button size="small" variant="ghost" onClick={() => setPanel('people')}>
            See all
          </Button>
        </div>
      )}
      {hands.length > 0 && (
        <p className="meeting-hands" role="status">
          ✋ {hands.map((p) => p.name).join(', ')} {hands.length > 1 ? 'have' : 'has'} a hand up
        </p>
      )}
      <div className={`meeting-body${panel ? ' with-panel' : ''}`}>
        <div className="meeting-stage">
          {watching ? (
            <>
              <ScreenStage id={watching.id} name={watching.name} />
              {s.screens.length > 1 && (
                <div className="filters" role="group" aria-label="Whose screen">
                  {s.screens.map((x) => (
                    <button key={x.id} type="button" aria-pressed={x.id === watching.id} onClick={() => watchMeetingScreen(x.id)}>
                      {x.name}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <Gallery people={s.people} photos={Boolean(member)} />
          )}
        </div>
        {panel === 'breakouts' && member && (
          <BreakoutsPanel
            s={s}
            member={member}
            breakouts={breakouts}
            names={names.current}
            onClose={() => setPanel(null)}
            onChange={setBreakouts}
            onVisit={(n) => void go(n)}
            onProblem={setProblem}
          />
        )}
        {panel === 'people' && (
          <aside className="meeting-panel" aria-label="People">
            <div className="meeting-panel-head">
              <strong>People</strong>
              <Button size="small" variant="ghost" icon="x" aria-label="Close" onClick={() => setPanel(null)} />
            </div>
            {member && waiting.length > 0 && (
              <section>
                <h2>
                  Waiting <span className="muted">{waiting.length}</span>
                </h2>
                <ul className="meeting-people">
                  {waiting.map((g) => (
                    <li key={g.id}>
                      <span className="grow">{g.name}</span>
                      <Button size="small" variant="primary" onClick={() => void answer(g.id, true)}>
                        Let in
                      </Button>
                      <Button size="small" variant="ghost" onClick={() => void answer(g.id, false)}>
                        No
                      </Button>
                    </li>
                  ))}
                </ul>
                {waiting.length > 1 && (
                  <Button size="small" onClick={() => void act(async () => (await meetingsApi.admitAll(member.tenant, member.id), setWaiting([])))}>
                    Let everyone in
                  </Button>
                )}
              </section>
            )}
            <section>
              <h2>
                In the meeting <span className="muted">{s.people.length}</span>
              </h2>
              <ul className="meeting-people">
                {s.people.map((p) => (
                  <li key={p.id}>
                    <span className="grow">
                      {p.hand && '✋ '}
                      {p.name}
                      {p.me && ' (you)'}
                      {p.guest && <span className="muted"> · guest</span>}
                      {member?.webinar && p.publish && !p.me && <span className="muted"> · presenter</span>}
                    </span>
                    {p.publish && <Icon name={p.muted ? 'micOff' : 'mic'} size={15} />}
                    {member?.canChange && !p.me && (
                      <span className="meeting-host-actions">
                        {p.publish && !p.muted && (
                          <Button size="small" variant="ghost" onClick={() => void act(() => meetingsApi.mute(member.tenant, member.id, p.id))}>
                            Mute
                          </Button>
                        )}
                        {member.webinar && (
                          <Button
                            size="small"
                            variant="ghost"
                            onClick={() =>
                              void act(async () => {
                                const on = !presenters.includes(p.id);
                                setPresenters((await meetingsApi.presenter(member.tenant, member.id, p.id, on)).presenters);
                              })
                            }
                          >
                            {presenters.includes(p.id) ? 'Stop presenting' : 'Make presenter'}
                          </Button>
                        )}
                        <Button size="small" variant="ghost" onClick={() => void act(() => meetingsApi.remove(member.tenant, member.id, p.id))}>
                          Remove
                        </Button>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        )}
      </div>
      {(problem || s.micProblem || s.cameraProblem || s.shareProblem) && (
        <div className="meeting-problems">
          <Notice tone="warn">{problem ?? s.micProblem ?? s.cameraProblem ?? s.shareProblem}</Notice>
        </div>
      )}
      <footer className="meeting-controls" role="toolbar" aria-label="Meeting controls">
        {s.publish ? (
          <>
            <Button variant={s.muted ? 'danger' : 'secondary'} icon={s.muted ? 'micOff' : 'mic'} disabled={s.phase !== 'connected'} onClick={() => void toggleMeetingMute()}>
              {s.muted ? 'Unmute' : 'Mute'}
            </Button>
            <Button icon={s.camera ? 'video' : 'videoOff'} disabled={s.phase !== 'connected'} onClick={() => void toggleMeetingCamera()}>
              {s.camera ? 'Stop video' : 'Start video'}
            </Button>
            {s.canShare && (
              <Button icon="screen" disabled={s.phase !== 'connected'} onClick={() => void toggleMeetingShare()}>
                {s.sharing ? 'Stop sharing' : 'Share screen'}
              </Button>
            )}
          </>
        ) : (
          <span className="muted meeting-watching">You’re watching and listening.</span>
        )}
        <Button icon="smile" aria-pressed={s.hand} disabled={s.phase !== 'connected'} onClick={() => void raiseHand()}>
          {s.hand ? 'Lower hand' : 'Raise hand'}
        </Button>
        <Button icon="users" aria-pressed={panel === 'people'} onClick={() => setPanel(panel === 'people' ? null : 'people')}>
          People{member && waiting.length ? ` · ${waiting.length} waiting` : ''}
        </Button>
        {canRecord && (
          <Button variant={s.recording ? 'danger' : 'secondary'} icon={s.recording ? 'stop' : 'video'} disabled={recBusy || s.phase !== 'connected'} onClick={() => void record(!s.recording)}>
            {s.recording ? 'Stop recording' : 'Record'}
          </Button>
        )}
        {canBreak && (
          <Button icon="grid" aria-pressed={panel === 'breakouts'} onClick={() => setPanel(panel === 'breakouts' ? null : 'breakouts')}>
            Breakout rooms{breakouts ? ` · ${breakouts.rooms.length} open` : ''}
          </Button>
        )}
      </footer>
    </main>
  );
}

/** Shuffled, so "Automatically" doesn't put the same people together every time. */
function shuffled<T>(list: T[]): T[] {
  const a = [...list];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/**
 * The host's breakout rooms (Teampace 2708): how many, who goes where (automatically or by hand),
 * Open rooms; then each room with its people, Join to visit, moving someone, and Bring everyone back.
 */
function BreakoutsPanel({
  s,
  member,
  breakouts,
  names,
  onClose,
  onChange,
  onVisit,
  onProblem
}: {
  s: MeetingState;
  member: NonNullable<Inside['member']>;
  breakouts: MeetingBreakouts | null;
  names: Map<string, string>;
  onClose: () => void;
  onChange: (b: MeetingBreakouts | null) => void;
  onVisit: (n: number) => void;
  onProblem: (why: string | null) => void;
}) {
  const others = s.people.filter((p) => !p.me);
  const [count, setCount] = useState(() => Math.min(20, Math.max(2, Math.ceil(others.length / 4))));
  const [byHand, setByHand] = useState(false);
  const [hand, setHand] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const act = async (what: () => Promise<{ breakouts: MeetingBreakouts | null }>) => {
    setBusy(true);
    onProblem(null);
    try {
      onChange((await what()).breakouts);
    } catch (err) {
      onProblem(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const open = () => {
    let assign: Record<string, number> = {};
    if (byHand) assign = Object.fromEntries(others.map((p) => [p.id, hand[p.id] ?? 0]));
    else shuffled(others).forEach((p, i) => (assign[p.id] = (i % count) + 1));
    void act(() => meetingsApi.openBreakouts(member.tenant, member.id, { rooms: count, assign }));
  };
  const nameOf = (id: string) => names.get(id) ?? 'Someone';
  const roomChoice = (value: number, onPick: (n: number) => void, label: string, rooms: { n: number; name: string }[]) => (
    <select className="input meeting-room-pick" aria-label={label} value={value} disabled={busy} onChange={(e) => onPick(Number(e.target.value))}>
      <option value={0}>Main room</option>
      {rooms.map((r) => (
        <option key={r.n} value={r.n}>
          {r.name}
        </option>
      ))}
    </select>
  );
  const assigned = new Set((breakouts?.rooms ?? []).flatMap((r) => r.people));
  return (
    <aside className="meeting-panel" aria-label="Breakout rooms">
      <div className="meeting-panel-head">
        <strong>Breakout rooms</strong>
        <Button size="small" variant="ghost" icon="x" aria-label="Close" onClick={onClose} />
      </div>
      {!breakouts ? (
        <div className="stack meeting-breakout-setup">
          <div className="field">
            <label htmlFor="breakout-count">Rooms</label>
            <input id="breakout-count" className="input" type="number" min={1} max={20} value={count} onChange={(e) => setCount(Math.min(20, Math.max(1, Number(e.target.value) || 1)))} />
          </div>
          <fieldset className="choice-row" aria-label="Who goes where">
            <label>
              <input type="radio" name="split" checked={!byHand} onChange={() => setByHand(false)} /> Automatically
            </label>
            <label>
              <input type="radio" name="split" checked={byHand} onChange={() => setByHand(true)} /> By hand
            </label>
          </fieldset>
          {byHand && (
            <ul className="meeting-people">
              {others.map((p) => (
                <li key={p.id}>
                  <span className="grow">{p.name}</span>
                  {roomChoice(hand[p.id] ?? 0, (n) => setHand((h) => ({ ...h, [p.id]: n })), `Room for ${p.name}`, Array.from({ length: count }, (_, i) => ({ n: i + 1, name: `Room ${i + 1}` })))}
                </li>
              ))}
            </ul>
          )}
          <p className="muted small">
            {others.length ? `${others.length} ${others.length === 1 ? 'person' : 'people'} move after a 10-second countdown. You stay here and can visit any room.` : 'Nobody else is here yet.'}
          </p>
          <Button variant="primary" disabled={busy || !others.length} onClick={open}>
            Open rooms
          </Button>
        </div>
      ) : (
        <>
          {breakouts.rooms.map((r) => (
            <section key={r.n}>
              <h3 className="meeting-room-head">
                {r.name} <span className="muted">· {r.people.length === 1 ? '1 person' : `${r.people.length} people`}</span>
              </h3>
              <ul className="meeting-people">
                {r.people.map((id) => (
                  <li key={id}>
                    <span className="grow">{nameOf(id)}</span>
                    {roomChoice(r.n, (n) => void act(() => meetingsApi.moveTo(member.tenant, member.id, id, n)), `Move ${nameOf(id)}`, breakouts.rooms)}
                  </li>
                ))}
              </ul>
              {s.room !== r.n && (
                <Button size="small" disabled={busy} onClick={() => onVisit(r.n)}>
                  Join {r.name}
                </Button>
              )}
            </section>
          ))}
          {others.some((p) => !assigned.has(p.id)) && s.room === 0 && (
            <section>
              <h2>In the main room</h2>
              <ul className="meeting-people">
                {others
                  .filter((p) => !assigned.has(p.id))
                  .map((p) => (
                    <li key={p.id}>
                      <span className="grow">{p.name}</span>
                      {roomChoice(0, (n) => void act(() => meetingsApi.moveTo(member.tenant, member.id, p.id, n)), `Move ${p.name}`, breakouts.rooms)}
                    </li>
                  ))}
              </ul>
            </section>
          )}
          <div className="row-actions meeting-breakout-end">
            {s.room > 0 && (
              <Button disabled={busy} onClick={() => onVisit(0)}>
                Back to the main room
              </Button>
            )}
            <Button variant="primary" disabled={busy} onClick={() => void act(() => meetingsApi.closeBreakouts(member.tenant, member.id))}>
              Bring everyone back
            </Button>
          </div>
        </>
      )}
    </aside>
  );
}
